<template>
    <b-container fluid="true" class="py-3">
        <b-row no-gutters>
            <b-col v-if="shownItemsCount === 0"
                   order="2" order-sm="1" cols="12" sm="11"
            >
                <div class="text-center h4 gray p-4">{{ $t('sensors-page.please-select-chart') }}</div>
            </b-col>

            <b-col v-else order="2" order-sm="1" cols="12" sm="11">
                {{ /* charts */ }}
                <b-row no-gutters>
                    <template
                        v-for="(activeItem, index) in availableSensors"
                    >
                        <ChartColumnWrapper
                            v-if="activeItem.show === true"
                            :active-item="activeItem"
                            :key="`aig-${index}`"
                        ></ChartColumnWrapper>
                    </template>
                </b-row>
            </b-col>

            {{ /* side buttons to show graphics */ }}
            <b-col cols="12" sm="1" order="1" order-sm="2" class="narrow-column-btn-wrapper">
                <WhiteIconButton
                    v-for="(item, index) in availableSensors"
                    :icon="item.icon"
                    :emit-this="'toggle-graphics-tab'"
                    v-on:toggle-graphics-tab="() => toggleGraphicsTab(item)"
                    :tooltip="item.title"
                    :tooltip-placement="'left'"
                    :active="item.show"
                    :key="`wib-${index}`"
                ></WhiteIconButton>
            </b-col>
        </b-row>
    </b-container>
</template>

<script>
    // utils
    import {mapGetters} from "vuex";
    // mixins
    
    import chartSafeTooltipMixin from "../../mixins/chartSafeTooltipMixin";
    import chartContainerMixinOnly from "../../mixins/chartContainerMixinOnly";
    // constants
    import {
        sensorsGraphics,
        showPlotLineFor,
        iconsUnits,
        autoStartSensors,
        autoStartTimeFrame,
        showTripleDataSensors,
        excludedSensors
    } from "../../constants/sensors-constants";
    import {defaultMeasuringProfile} from "../../constants/chart-constants";
    // components
    import WhiteIconButton from "../shared/WhiteIconButton";
    import ChartColumnWrapper from "./ChartColumnWrapper";

    /**
     * Container for all controller graphics. This is one tab's content.
     * There are 4 in total.
     * Also contains switcher buttons to the right.
     */

    export default {
        name: "ChartControllerContainer",
        mixins: [chartSafeTooltipMixin, chartContainerMixinOnly],
        components: {
            WhiteIconButton,
            ChartColumnWrapper
        },
        data() {
            return {
                availableSensors: [],
                containsMultiChartItem: false,
                tabTitle: "sensors-page.tab-title-generic"
            }
        },
        created() {
            this.setAvailableSensors();
        },
        computed: {
            ...mapGetters(['getDamData', 'isStaff']),
            shownItemsCount() {
                return this.availableSensors.filter(item => item.show === true).length;
            }
        },
        methods: {
            /**
             * Goes through the pre-defined set of sensors,
             * check what is available and build an array of objects.
             * Set the current availableSensors at data.
             * Each sensor has active flag.
             */
            setAvailableSensors() {
                // get the type of the user
                const isUserStaff = this.isStaff;

                const keys = Object.keys(this.getDamData);

                const availableSensors = [];
                // NOTE: if the sensorsGraphics list is not up to date with  the keys, may lead to
                // missing sensors.
                for (let sensor of sensorsGraphics) {
                    if (keys.includes(sensor) && (isUserStaff || !excludedSensors.includes(sensor))) {
                        // build a list with all available sensors (used for icons and graphics)
                        availableSensors.push({
                            name: sensor,
                            title: this.safeGetTooltip(sensor),
                            value: this.getDamData[sensor],
                            metric: iconsUnits?.[sensor]?.unit || '',
                            icon: iconsUnits?.[sensor]?.icon || '',
                            show: autoStartSensors.includes(sensor),
                            alternative: false,
                            autoStart: autoStartSensors.includes(sensor),
                            autoStartTimeFrame: autoStartTimeFrame,
                            showTripleData: showTripleDataSensors.includes(sensor),
                            showPlotLine: showPlotLineFor.includes(sensor),
                            measuringProfile: defaultMeasuringProfile,
                            containsMultiChartItem: this.containsMultiChartItem
                        });
                    }
                }
                this.availableSensors = availableSensors;
            },
            /**
             * Iterates trough the available sensors to set
             * active for the clicked item.
             * Graphic blocks are shown for FILTERED (active) items.
             * @param {object} item
             */
            toggleGraphicsTab(item) {
                for (let index = 0; index < this.availableSensors.length; index++) {
                    if (this.availableSensors[index].name === item.name) {
                        this.availableSensors[index].show = !this.availableSensors[index].show;
                        break;
                    }
                }
            }
        }
    }
</script>

<style lang="scss" scoped>
    @import "../../assets/scss/variables";
    @import "../../assets/scss/mixins";

    @media screen and (min-width: 763px) {
        .narrow-column-btn-wrapper {
            min-width: 44px;
            text-align: end;
            display: flex;
            flex-direction: column;
            align-items: flex-end;
        }
    }

    @media screen and (max-width: 575px) {
        .narrow-column-btn-wrapper {
            display: flex;
            flex-direction: row;
            justify-content: center;
            flex-wrap: wrap;
        }
    }
</style>